/**
 * TypeScript, form field, and SQL type mappings for schema fields
 */

import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships.js'
import { DRIZZLE_DEFAULTS } from '@core-engine/schema/type-mappers/drizzle.js'
import { assertSupportedSchemaFieldTree } from '@core-engine/schema/type-mappers/validation.js'
import type { SchemaField } from '@core-engine/schema/types.js'
import { quotePropertyName, singularize, toPascalCase } from '@core-engine/utils/string.js'

// ============================================================================
// Schema Field → TypeScript Type (for actions generator)
// ============================================================================

/**
 * Map a schema field type to TypeScript type
 * @param field - The schema field
 * @param mode - 'input' for form input types, 'output' for database output types
 * @returns The TypeScript type as a string
 */
export function toTypeScriptType(field: SchemaField, mode: 'input' | 'output' = 'output'): string {
  assertSupportedSchemaFieldTree(field)

  switch (field.type) {
    case 'uuid':
    case 'serial':
      return 'string'
    case 'number':
    case 'decimal':
      return 'number'
    case 'boolean':
      return 'boolean'
    case 'string':
    case 'email':
    case 'varchar':
    case 'text':
    case 'markdown':
    case 'richtext':
    case 'date':
    case 'time':
    case 'timestamp':
    case 'image':
    case 'video':
    case 'media':
    case 'icon':
      return 'string'
    case 'select':
      return field.multiple ? 'string[]' : 'string'
    case 'relationship':
      if (mode === 'input') {
        return field.multiple ? 'string[]' : 'string'
      }
      if (isUserRelationshipTarget(field.relationship)) {
        return field.multiple ? 'UserData[]' : 'UserData | null'
      }
      if (field.relationship) {
        const relationshipSingular = singularize(field.relationship)
        const relationshipPascal = toPascalCase(relationshipSingular)
        return field.multiple ? `${relationshipPascal}Data[]` : `${relationshipPascal}Data | null`
      }
      return 'string'
    case 'group':
      if (field.fields && field.fields.length > 0) {
        const groupType = field.fields
          .map((f) => {
            const type = toTypeScriptType(f, mode)
            return `${quotePropertyName(f.name)}?: ${type}`
          })
          .join('; ')
        return `{ ${groupType} }`
      }
      return 'Record<string, unknown>'
    case 'list':
      if (field.fields && field.fields.length > 0) {
        const nestedType = field.fields
          .flatMap((f) => {
            const type = toTypeScriptType(f, 'input')
            const fields = [`${quotePropertyName(f.name)}?: ${type}`]
            if (f.hasIcon) {
              fields.push(`${quotePropertyName(`${f.name}Icon`)}?: string`)
            }
            return fields
          })
          .join('; ')
        return `Array<{ ${nestedType} }>`
      }
      return 'string[]'
    case 'gallery':
      return 'string[]'
    default:
      return 'string'
  }
}

// ============================================================================
// Schema Field → Form Field Type (for form generator)
// ============================================================================

/**
 * Map a schema field type to HTML form input type
 * @param field - The schema field
 * @returns The form input type (checkbox, textarea, number, date, etc.)
 */
export function toFormFieldType(field: SchemaField): string {
  assertSupportedSchemaFieldTree(field)

  switch (field.type) {
    case 'boolean':
      return 'checkbox'
    case 'email':
      return 'email'
    case 'text':
      return 'textarea'
    case 'markdown':
      return 'markdown'
    case 'richtext':
      return 'richtext'
    case 'number':
    case 'decimal':
      return 'number'
    case 'date':
      return 'date'
    case 'time':
      return 'time'
    case 'timestamp':
      return 'datetime-local'
    default:
      return 'text'
  }
}

// ============================================================================
// Schema Field → SQL Type (for raw SQL migrations)
// ============================================================================

/**
 * Map a schema field type to SQL column type
 * @param field - The schema field
 * @returns The SQL column type
 */
export function toSQLType(field: SchemaField): string {
  assertSupportedSchemaFieldTree(field)

  switch (field.type) {
    case 'uuid':
    case 'serial':
      return 'UUID'
    case 'string':
    case 'email':
    case 'varchar':
      return field.length
        ? `VARCHAR(${field.length})`
        : `VARCHAR(${DRIZZLE_DEFAULTS.VARCHAR_LENGTH})`
    case 'text':
    case 'markdown':
    case 'richtext':
    case 'image':
    case 'video':
    case 'media':
      return 'TEXT'
    case 'number':
      return 'INTEGER'
    case 'decimal':
      return `DECIMAL(${field.precision || DRIZZLE_DEFAULTS.DECIMAL_PRECISION}, ${field.scale || DRIZZLE_DEFAULTS.DECIMAL_SCALE})`
    case 'boolean':
      return 'BOOLEAN'
    case 'timestamp':
      return 'TIMESTAMP(3)'
    case 'date':
      return 'DATE'
    case 'time':
    case 'icon':
      return `VARCHAR(${DRIZZLE_DEFAULTS.VARCHAR_LENGTH})`
    case 'select':
      return field.multiple ? 'JSONB' : `VARCHAR(${DRIZZLE_DEFAULTS.VARCHAR_LENGTH})`
    case 'list':
    case 'gallery':
      return 'JSONB'
    case 'relationship':
      return isUserRelationshipTarget(field.relationship) ? 'TEXT' : 'UUID'
    default:
      return 'TEXT'
  }
}
